import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Grid,
  Stack,
  Tooltip,
} from '@mui/material';
import { Add, EditOutlined, People, TrendingUp, AccountBalance, Assessment, VisibilityOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatNumber } from '../../../utils/generalFunctions';
import { StyledCellHeader } from '../../../components/StyledCellHeader';
import EmptyState from '../../../components/EmptyState';
import { EstadoChip, InitialsAvatar, SectionTitle } from './inversionistasUI';

const EMPTY_FORM = {
  nombre: '',
  identificacion: '',
  email: '',
  telefono: '',
  direccion: '',
  estado: 'activo',
};

const InversionistasList = ({ onRefresh }) => {
  const navigate = useNavigate();
  const [inversionistas, setInversionistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const idcompany = localStorage.getItem('idcompany');
  const usuarioId = localStorage.getItem('id');

  const headers = {
    'Content-Type': 'application/json',
    'X-Usuario-ID': usuarioId,
    'X-company-ID': idcompany,
  };

  useEffect(() => {
    fetchInversionistas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInversionistas = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/inversionistas/${idcompany}`,
        { headers }
      );
      if (response.ok) setInversionistas(await response.json());
    } catch (error) {
      console.error('Error fetching inversionistas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (inversionista = null) => {
    if (inversionista) {
      setEditingId(inversionista.id);
      setFormData({
        nombre: inversionista.nombre || '',
        identificacion: inversionista.identificacion || '',
        email: inversionista.email || '',
        telefono: inversionista.telefono || '',
        direccion: inversionista.direccion || '',
        estado: inversionista.estado || 'activo',
      });
    } else {
      setEditingId(null);
      setFormData(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    try {
      const url = editingId
        ? `${process.env.REACT_APP_SERVER_URL}/api/inversionistas/${editingId}`
        : `${process.env.REACT_APP_SERVER_URL}/api/inversionistas`;
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({ ...formData, idcompany }),
      });

      if (response.ok) {
        fetchInversionistas();
        handleCloseDialog();
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Error saving inversionista:', error);
    }
  };

  if (loading) {
    return <Box sx={{ color: 'text.secondary', py: 4 }}>Cargando...</Box>;
  }

  return (
    <Box>
      {inversionistas.length === 0 ? (
        <EmptyState
          icon={People}
          title="No hay inversionistas registrados"
          description="Registra tu primer inversionista y luego crea sus acuerdos de inversión."
          features={[
            { icon: AccountBalance, title: 'Acuerdos de Inversión', description: 'Registra el capital aportado por cada inversionista' },
            { icon: TrendingUp, title: 'Rendimiento Fijo', description: 'Define un porcentaje de rendimiento sobre el capital' },
            { icon: Assessment, title: 'Ganancia General', description: 'Compara contra la ganancia general de la empresa' },
          ]}
          buttonText="Crear Nuevo Inversionista"
          onButtonClick={() => handleOpenDialog()}
          helpText="¿Necesitas ayuda? Contacta a soporte técnico para guiarte en el proceso"
        />
      ) : (
        <>
          <SectionTitle
            action={
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{ fontWeight: 600, textTransform: 'none' }}
                color="primarybutton"
              >
                Nuevo Inversionista
              </Button>
            }
          >
            Inversionistas ({inversionistas.length})
          </SectionTitle>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}
          >
            <Table sx={{ '& td, & th': { borderColor: 'divider' } }}>
              <TableHead>
                <TableRow>
                  <StyledCellHeader>Inversionista</StyledCellHeader>
                  <StyledCellHeader>Identificación</StyledCellHeader>
                  <StyledCellHeader>Teléfono</StyledCellHeader>
                  <StyledCellHeader align="center">Acuerdos activos</StyledCellHeader>
                  <StyledCellHeader align="right">Capital invertido</StyledCellHeader>
                  <StyledCellHeader align="right">Rendimiento mensual</StyledCellHeader>
                  <StyledCellHeader align="center">Estado</StyledCellHeader>
                  <StyledCellHeader align="center">Acciones</StyledCellHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {inversionistas.map((inv) => (
                  <TableRow
                    key={inv.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/gestion_comercial/inversionistas/detalle/${inv.id}`)}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <InitialsAvatar name={inv.nombre} size={36} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{inv.nombre}</Typography>
                          {inv.email && (
                            <Typography variant="caption" color="text.secondary">{inv.email}</Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{inv.identificacion || '—'}</TableCell>
                    <TableCell>{inv.telefono || '—'}</TableCell>
                    <TableCell align="center">{inv.acuerdos_activos || 0}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(inv.capital_invertido || 0)}</TableCell>
                    <TableCell align="right">{formatNumber(inv.rendimiento_mensual || 0)}</TableCell>
                    <TableCell align="center"><EstadoChip estado={inv.estado} /></TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Ver detalles">
                          <IconButton size="small" onClick={() => navigate(`/gestion_comercial/inversionistas/detalle/${inv.id}`)}>
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenDialog(inv)}>
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? 'Editar Inversionista' : 'Nuevo Inversionista'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item size={6} xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign={'left'} fontWeight={600}>Nombre</Typography>
              <TextField
                sx={{ mt: -1 }}
                fullWidth
                placeholder="Nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
            </Grid>
            <Grid item size={6} xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign={'left'} fontWeight={600}>Identificación</Typography>
              <TextField
                fullWidth
                placeholder="Identificación"
                sx={{ mt: -1 }}
                value={formData.identificacion}
                onChange={(e) => setFormData({ ...formData, identificacion: e.target.value })}
              />
            </Grid>
            <Grid item size={6} xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign={'left'} fontWeight={600}>Email</Typography>
              <TextField
                fullWidth
                placeholder="Email"
                sx={{ mt: -1 }}
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item size={6} xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign={'left'} fontWeight={600}>Teléfono</Typography>
              <TextField
                fullWidth
                placeholder="Teléfono"
                sx={{ mt: -1 }}
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
              />
            </Grid>
            <Grid item size={6} xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign={'left'} fontWeight={600}>Estado</Typography>
                <Select
                  sx={{ mt: -1, backgroundColor: 'white' }}
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                >
                  <MenuItem value="activo">Activo</MenuItem>
                  <MenuItem value="inactivo">Inactivo</MenuItem>
                  <MenuItem value="suspendido">Suspendido</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item size={6} xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom textAlign={'left'} fontWeight={600}>Dirección</Typography>
              <TextField
                fullWidth
                placeholder="Ingrese la dirección"
                sx={{ mt: -1 }}
                multiline
                rows={2}
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" color="primarybutton" sx={{ fontWeight: 600, textTransform: 'none' }} disabled={!formData.nombre.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InversionistasList;
